import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import {
  SectionAccordionBodyKeyContext,
  SectionAccordionRowBoundariesContext,
  SectionAccordionViewportContext,
} from "#section-accordion-viewport";
import { MeasuredRowIndex } from "#measured-row-index";

/** Variable-height counterpart of the fixed grid window; measurements never clip row contents. */
export function useSectionAccordionMeasuredRowWindow({
  estimatedRowHeight,
  geometryKey,
  keys,
  onOffsetCorrection,
}: {
  estimatedRowHeight: number;
  /** Width/font-scale changes invalidate measurements; ordinary data reordering preserves them. */
  geometryKey: string;
  keys: readonly string[];
  onOffsetCorrection?: (offset: number) => void;
}) {
  const isFocused = useIsNavigationFocused();
  const viewport = useContext(SectionAccordionViewportContext);
  const bodyKey = useContext(SectionAccordionBodyKeyContext);
  const reportBoundaries = useContext(SectionAccordionRowBoundariesContext);
  const committed = useRef<{
    index: MeasuredRowIndex;
    isFocused: boolean;
    onOffsetCorrection: typeof onOffsetCorrection;
  } | null>(null);
  const index = useMemo(
    () => new MeasuredRowIndex(keys, estimatedRowHeight, geometryKey, committed.current?.index),
    [keys, estimatedRowHeight, geometryKey],
  );
  useLayoutEffect(() => {
    if (!isFocused || reportBoundaries === null) {
      return;
    }
    const report = () =>
      reportBoundaries(
        Array.from({ length: keys.length }, (_, row) => index.offset(row + 1)),
        estimatedRowHeight,
      );
    report();
    return index.subscribe(report);
  }, [estimatedRowHeight, index, isFocused, keys.length, reportBoundaries]);

  useLayoutEffect(() => {
    const previous = committed.current;
    committed.current = { index, isFocused, onOffsetCorrection };
    // Keep the same first visible row and within-row offset after resizing. Reordering deliberately
    // keeps the existing numerical scroll offset, matching the previous fully rendered list.
    if (
      previous &&
      isFocused &&
      viewport &&
      bodyKey &&
      onOffsetCorrection &&
      previous.index.geometryKey !== geometryKey
    ) {
      const baseline = viewport.readGeometryChangeSnapshot(geometryKey);
      const body = baseline.bodies.get(bodyKey);
      if (body && baseline.height > 0 && baseline.offset >= body.y) {
        let difference = 0;
        if (baseline.offset >= body.y + previous.index.totalHeight) {
          difference = index.totalHeight - previous.index.totalHeight;
        } else {
          const anchor = previous.index.rowAt(baseline.offset - body.y);
          const key = previous.index.keys[anchor];
          const next = key === undefined ? undefined : index.positions.get(key);
          if (next !== undefined) {
            difference = index.offset(next) - previous.index.offset(anchor);
          }
        }
        if (difference !== 0) {
          const current = viewport.getViewport();
          const corrected = Math.max(0, current.offset + difference);
          viewport.setViewport(current.height, corrected);
          onOffsetCorrection(corrected);
        }
      }
    }
  }, [bodyKey, geometryKey, index, isFocused, onOffsetCorrection, viewport]);
  useLayoutEffect(
    () => () => {
      committed.current = null;
    },
    [],
  );

  const getSnapshot = useCallback(() => {
    let start = 0;
    let end = keys.length;
    if (!isFocused) {
      end = 0;
    } else if (viewport && bodyKey) {
      const body = viewport.getBody(bodyKey);
      const { height, offset } = viewport.getViewport();
      if (!body || height <= 0) {
        end = Math.min(12, keys.length);
      } else {
        const relativeOffset = offset - body.y;
        start = index.rowAt(relativeOffset - height);
        end = Math.max(start, index.endAt(relativeOffset + height * 2));
      }
    }
    return `${start}:${end}:${index.offset(start)}:${index.totalHeight - index.offset(end)}`;
  }, [bodyKey, index, isFocused, keys.length, viewport]);
  const subscribe = useCallback(
    (listener: () => void) => {
      const stopIndex = index.subscribe(listener);
      const stopViewport = isFocused ? viewport?.subscribe(listener) : undefined;
      return () => {
        stopIndex();
        stopViewport?.();
      };
    },
    [index, isFocused, viewport],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [start = 0, end = 0, topSpacerHeight = 0, bottomSpacerHeight = 0] = snapshot
    .split(":")
    .map(Number);
  const measure = useCallback(
    (key: string, height: number) => {
      const current = committed.current;
      // Native layout callbacks queued before blur/unmount or data replacement must not overwrite
      // current geometry, issue an offscreen scroll or retain a retired payload.
      if (!current?.isFocused || current.index !== index) {
        return;
      }
      const position = index.positions.get(key);
      if (position === undefined) {
        return;
      }
      const oldBottom = index.offset(position + 1);
      const difference = index.measure(key, height);
      if (difference === 0) {
        return;
      }
      if (viewport && bodyKey && current.onOffsetCorrection) {
        const body = viewport.getBody(bodyKey);
        const visible = viewport.getViewport();
        if (body && visible.height > 0 && body.y + oldBottom <= visible.offset) {
          const offset = Math.max(0, visible.offset + difference);
          viewport.setViewport(visible.height, offset);
          current.onOffsetCorrection(offset);
        }
      }
      index.publish();
    },
    [bodyKey, index, viewport],
  );
  return { bottomSpacerHeight, end, measure, start, topSpacerHeight };
}
